/**
 * Button — legacy API over Astryx (see web/src/components/ui/Button.tsx).
 *
 * Real `<button>`s render Astryx's Button (themed by shared/theme/hireos.ts).
 * The `href`, `to`, and `link` forms keep the hand-rolled anchor/Link
 * rendering — Astryx has no link-shaped Button, and navigation isn't a
 * Button there. Their appearance is the co-located module, written against
 * the tokens in src/styles.css (#443).
 */
import { Button as AstryxButton } from "@astryxdesign/core/Button";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";
import { cx } from "@shared/cx";
import styles from "./Button.module.css";

interface ButtonBaseProps {
  variant?: "primary" | "secondary" | "outline" | "danger" | "link" | "ghost";
  size?: "sm" | "md" | "lg" | "icon";
  loading?: boolean;
  children: ReactNode;
  className?: string;
}

interface ButtonAsButton
  extends
    ButtonBaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> {
  href?: never;
  target?: never;
  rel?: never;
  to?: never;
}

interface ButtonAsLink extends ButtonBaseProps {
  href: string;
  target?: string;
  rel?: string;
  to?: never;
  onClick?: never;
  disabled?: never;
  type?: never;
}

interface ButtonAsRouterLink extends ButtonBaseProps {
  /** react-router destination — renders an in-app `<Link>` (no page reload). */
  to: string;
  href?: never;
  target?: never;
  rel?: never;
  onClick?: never;
  disabled?: never;
  type?: never;
}

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsRouterLink;

/**
 * Every button but the one that commits is bare, as Granola's are: words
 * in ink with no box, no border and no fill — so a primary or a destructive
 * button is the only filled thing on a screen and reads as the step to take
 * (DESIGN-SYSTEM.md, Buttons). `secondary` and `outline` keep their names
 * at the call sites and draw as Astryx's `ghost`.
 */
const ASTRYX_VARIANT = {
  primary: "primary",
  secondary: "ghost",
  danger: "destructive",
  ghost: "ghost",
  outline: "ghost",
} as const;

type Variant = NonNullable<ButtonBaseProps["variant"]>;
type Size = NonNullable<ButtonBaseProps["size"]>;

const LINK_VARIANT: Record<Variant, string | undefined> = {
  primary: styles.primary,
  secondary: styles.secondary,
  outline: styles.outline,
  danger: styles.danger,
  link: styles.link,
  ghost: styles.ghost,
};

const LINK_SIZE: Record<Size, string | undefined> = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
  icon: styles.icon,
};

function linkClasses(variant: Variant, size: Size, className: string) {
  // A `link` takes no size padding: it is text sitting in the anchor's box,
  // not a box of its own, and `.link` zeroes the padding anyway.
  return cx(
    styles.anchor,
    LINK_VARIANT[variant],
    variant !== "link" && LINK_SIZE[size],
    className,
  );
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  className = "",
  children,
  ...props
}: ButtonProps) {
  if ("to" in props && props.to) {
    const { to } = props as ButtonAsRouterLink;
    return (
      <Link to={to} className={linkClasses(variant, size, className)}>
        {children}
      </Link>
    );
  }

  if ("href" in props && props.href) {
    const { href, target, rel, ...rest } = props as ButtonAsLink;
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={linkClasses(variant, size, className)}
        {...(rest as Record<string, unknown>)}
      >
        {children}
      </a>
    );
  }

  const {
    disabled,
    type,
    onClick,
    "aria-label": ariaLabel,
    ...buttonProps
  } = props as ButtonAsButton;

  if (variant === "link") {
    return (
      <button
        type={type ?? "button"}
        onClick={onClick}
        disabled={disabled || loading}
        className={cx(styles.link, className)}
        {...buttonProps}
      >
        {children}
      </button>
    );
  }

  const label = typeof children === "string" ? children : (ariaLabel ?? "");

  return (
    <AstryxButton
      label={label}
      variant={ASTRYX_VARIANT[variant] as "primary"}
      size={size === "icon" ? "sm" : size}
      isIconOnly={size === "icon"}
      icon={size === "icon" ? children : undefined}
      isLoading={loading}
      isDisabled={disabled}
      type={type}
      onClick={onClick}
      className={className}
      {...(buttonProps as Record<string, unknown>)}
    >
      {typeof children === "string" || size === "icon" ? undefined : (
        // Mixed children (icon + text): the Astryx reset makes svg
        // display:block, which stacks it above the text inside Astryx's label
        // span — keep a row. See the module for why the row is `flex` rather
        // than `inline-flex` (#213).
        <span className={styles.mixedChildren}>{children}</span>
      )}
    </AstryxButton>
  );
}
